'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { fetchStats } from '@/lib/supabase/services';
import type { AppStats } from '@/lib/supabase/services';

export default function AlertCards() {
  const [stats, setStats] = useState<AppStats | null>(null);
  const [dismissed, setDismissed] = useState<number[]>([]);

  useEffect(() => {
    fetchStats().then(setStats);
  }, []);

  if (!stats) return null;

  const staleLocaux = stats.locauxSansDocs;

  const alerts = [
    {
      id: 1,
      type: 'warning',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.07 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      title: `${staleLocaux} locaux sans documents`,
      description: "Fiches incomplètes — Risque de perte d\'opportunités de matching",
      action: 'Voir les locaux',
      href: '/liste-des-locaux',
    },
    {
      id: 2,
      type: 'error',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      title: `${stats.locauxSansDocs} fiches avec documents manquants`,
      description: `${stats.docsManquants} doc${stats.docsManquants !== 1 ? 's' : ''} manquant${stats.docsManquants !== 1 ? 's' : ''} · ${stats.docsExpires} expiré${stats.docsExpires !== 1 ? 's' : ''} — Matching incomplet`,
      action: 'Compléter les fiches',
      href: '/documents',
    },
    {
      id: 3,
      type: 'info',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      title: `${stats.dealsEnNegociation} deals actifs en cours`,
      description: `${stats.enseignesEnRecherche} enseignes en recherche · ${stats.visitesPlanifiees} visites planifiées`,
      action: 'Voir les matchs',
      href: '/matching',
    },
  ];

  const alertStyles = {
    warning: {
      bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600',
      title: 'text-amber-900', desc: 'text-amber-700', action: 'text-amber-700 hover:text-amber-900',
    },
    error: {
      bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600',
      title: 'text-red-900', desc: 'text-red-700', action: 'text-red-700 hover:text-red-900',
    },
    info: {
      bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600',
      title: 'text-blue-900', desc: 'text-blue-700', action: 'text-blue-700 hover:text-blue-900',
    },
  };

  const visible = alerts.filter(a => !dismissed.includes(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
      {visible.map(alert => {
        const s = alertStyles[alert.type as keyof typeof alertStyles];
        return (
          <div key={alert.id} className={`${s.bg} ${s.border} border rounded-xl p-4 flex gap-3 items-start animate-fade-in`}>
            <span className={`${s.icon} flex-shrink-0 mt-0.5`}>{alert.icon}</span>
            <div className="min-w-0 flex-1">
              <p className={`text-xs font-600 ${s.title}`}>{alert.title}</p>
              <p className={`text-xs ${s.desc} mt-0.5 leading-relaxed`}>{alert.description}</p>
              <Link href={alert.href} className={`text-xs font-600 ${s.action} mt-1.5 inline-block underline-offset-2 hover:underline`}>
                {alert.action} →
              </Link>
            </div>
            <button onClick={() => setDismissed(d => [...d, alert.id])} className={`${s.icon} opacity-60 hover:opacity-100 flex-shrink-0 transition-opacity`} aria-label="Fermer l'alerte">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
  );
}